/**
 * Print Convergence chapter — Canva page-05 Alignment & Execution.
 * Handshake hero, timing score + bar, print-native Do/Don't cards,
 * personal note with mountain seal, executive insight bar with pillars.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Check,
  Scale,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import heroAlignment from "../../../../assets/monthly-consultation/pdf/hero-alignment.png";
import noteMountains from "../../../../assets/monthly-consultation/pdf/alignment-note-mountains.png";
import { PdfChapterShell } from "../atoms";
import {
  ALIGNMENT_INSIGHT_PILLARS,
  alignmentBandLabel,
  alignmentInsightQuote,
  clipPdfText,
  collectUniqueLines,
} from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardShadow,
  cardWhite,
  charcoal,
  deepRed,
  displayFontFamily,
  gold,
  green,
  insightDark,
  mutedGray,
  paleTan,
  pinkRed,
  tanGold,
} from "../pdfTheme";

/** Tone for a print Do / Don't guidance card. */
type GuidanceTone = "do" | "dont";

/**
 * Props for one equal-height Do / Don't print card.
 */
interface PdfGuidanceCardProps {
  /** Constructive (do) or avoid (don't) column. */
  tone: GuidanceTone;
  /** Live guidance lines (already clipped, max 3). */
  lines: readonly string[];
  /** Body shown when the column has no lines. */
  emptyFallback: string;
}

/**
 * Soft tint from a 6-digit hex (12% alpha suffix) for row icon circles.
 *
 * @param color - Theme hex such as `#15803d`
 * @returns CSS color with alpha, or the card border token
 */
function tintHex(color: string): string {
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `${trimmed}1f`;
  }
  return border;
}

/**
 * Print-native Do / Don't card — header + icon rows inside one white panel.
 *
 * @param props - Tone, lines, and empty fallback
 * @returns One stretched guidance card
 */
const PdfGuidanceCard: React.FC<PdfGuidanceCardProps> = ({
  tone,
  lines,
  emptyFallback,
}) => {
  const isDo = tone === "do";
  const accent = isDo ? green : pinkRed;
  const title = isDo ? "Do this" : "Don't do this";
  const rowBg = isDo ? tintHex(mutedGray) : tintHex(pinkRed);

  return (
    <div
      className="mc-pdf-card"
      style={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        background: cardWhite,
        border: `1px solid ${border}`,
        borderRadius: 14,
        boxShadow: cardShadow,
        padding: "12px 14px",
        fontFamily: bodyFontFamily,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingBottom: 8,
          marginBottom: 8,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isDo ? (
            <Check size={12} color={cardWhite} strokeWidth={3} />
          ) : (
            <X size={12} color={cardWhite} strokeWidth={3} />
          )}
        </span>
        <p
          style={{
            margin: 0,
            fontFamily: displayFontFamily,
            fontSize: 16,
            fontWeight: 700,
            lineHeight: 1.2,
            color: charcoal,
          }}
        >
          {title}
        </p>
      </div>
      {lines.length > 0 ? (
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
              gap: 8,
              flex: "0 1 auto",
            }}
          >
            {lines.map((line, index) => (
            <li
              key={`${tone}-${String(index)}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: rowBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {isDo ? (
                  <Check size={11} color={green} strokeWidth={2} />
                ) : (
                  <X size={11} color={pinkRed} strokeWidth={2} />
                )}
              </span>
              <p
                style={{
                  margin: 0,
                  fontSize: 11,
                  lineHeight: 1.4,
                  color: charcoal,
                }}
              >
                {line}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ margin: 0, fontSize: 11, lineHeight: 1.4, color: mutedGray }}>
          {emptyFallback}
        </p>
      )}
    </div>
  );
};

/**
 * Props for the PDF convergence chapter.
 */
export interface PdfChapterConvergenceProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/**
 * Resolve the webpack URL for the Alignment handshake hero.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function alignmentHeroSrc(): string {
  return typeof heroAlignment === "string" ? heroAlignment.trim() : "";
}

/**
 * Resolve the webpack URL for the personal-note mountain seal.
 *
 * @returns Image URL, or empty string when missing
 */
function noteMountainsSrc(): string {
  return typeof noteMountains === "string" ? noteMountains.trim() : "";
}

/**
 * Fill color for the alignment score bar from the convergence band.
 * Clash uses Canva burgundy (`deepRed`); partial / aligned stay warm / green.
 *
 * @param band - clash / partial / aligned
 * @returns Theme hex
 */
function alignmentBarColor(band: "aligned" | "partial" | "clash"): string {
  if (band === "aligned") {
    return green;
  }
  if (band === "partial") {
    return tanGold;
  }
  return deepRed;
}

/**
 * Clamp a convergence score into 0–100 for display and the bar.
 *
 * @param value - Raw score
 * @returns Safe integer percentage
 */
function clampScore(value: number): number {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }
  if (value < 0) {
    return 0;
  }
  if (value > 100) {
    return 100;
  }
  return Math.round(value);
}

/** Pillar key → Lucide icon (Canva executive bar right column). */
const PILLAR_ICON: Record<"timely" | "aligned" | "compound", LucideIcon> = {
  timely: Target,
  aligned: Scale,
  compound: BarChart3,
};

/**
 * Alignment & Execution — one A4 sheet of timing score and do/don't.
 */
export const PdfChapterConvergence: React.FC<PdfChapterConvergenceProps> = ({
  bundle,
  pageNumber,
}) => {
  const hero = alignmentHeroSrc();
  const mountains = noteMountainsSrc();
  const label =
    typeof bundle.convergence.label === "string"
      ? bundle.convergence.label.trim()
      : "";
  const summary =
    typeof bundle.convergence.summary === "string"
      ? bundle.convergence.summary.trim()
      : "";
  const score = clampScore(bundle.convergence.score);
  const band = bundle.convergence.band;
  const bandLabel = alignmentBandLabel(band);
  const barColor = alignmentBarColor(band);

  const keyActions = Array.isArray(bundle.briefing.guidance.keyActions)
    ? bundle.briefing.guidance.keyActions
    : [];
  const watchOut = Array.isArray(bundle.briefing.guidance.watchOut)
    ? bundle.briefing.guidance.watchOut
    : [];
  const actionSteps = Array.isArray(bundle.rarity.actionSteps)
    ? bundle.rarity.actionSteps
    : [];

  const doThis = collectUniqueLines(
    [
      keyActions,
      typeof bundle.monthContract.primaryMove === "string"
        ? bundle.monthContract.primaryMove
        : "",
      actionSteps,
      typeof bundle.failureMode.exitMove === "string"
        ? bundle.failureMode.exitMove
        : "",
    ],
    3
  ).map((line) => clipPdfText(line, 110));

  const dontDo = collectUniqueLines(
    [
      watchOut,
      typeof bundle.monthContract.antiPattern === "string"
        ? bundle.monthContract.antiPattern
        : "",
    ],
    3
  ).map((line) => clipPdfText(line, 110));

  const profileName =
    typeof bundle.profileName === "string" ? bundle.profileName.trim() : "";
  const letterPara =
    Array.isArray(bundle.letter.bodyParagraphs) &&
    typeof bundle.letter.bodyParagraphs[0] === "string"
      ? bundle.letter.bodyParagraphs[0].trim()
      : "";
  const identityLine =
    typeof bundle.archetype.identityLine === "string"
      ? bundle.archetype.identityLine.trim()
      : "";
  const noteBody = clipPdfText(
    letterPara.length > 0 ? letterPara : identityLine,
    240
  );
  const noteTitle =
    profileName.length > 0
      ? `Personal note for ${profileName}`
      : "Personal note";
  const noteSignParts: string[] = [];
  if (profileName.length > 0) {
    noteSignParts.push(profileName);
  }
  noteSignParts.push(`M${String(bundle.lunarMonth)} ${String(bundle.solarYear)}`);
  const noteSign = noteSignParts.join(" - ");

  const insight = clipPdfText(
    alignmentInsightQuote(
      summary,
      typeof bundle.convergence.bandMeaning === "string"
        ? bundle.convergence.bandMeaning
        : "",
      typeof bundle.convergence.scoreExplainer === "string"
        ? bundle.convergence.scoreExplainer
        : ""
    ),
    180
  );

  return (
    <PdfChapterShell
      chapterId="convergence"
      eyebrow="Timing layer sync"
      title="Alignment & Execution"
      pageNumber={pageNumber}
      compact
      fillPage={true}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 18,
          flex: "1 1 auto",
          minHeight: 0,
          height: "100%",
          fontFamily: bodyFontFamily,
          justifyContent: "space-between",
        }}
      >
        {hero.length > 0 ? (
          <img
            src={hero}
            alt=""
            className="mc-pdf-hero-img mc-pdf-hero-img--alignment"
            style={{ flexShrink: 0 }}
          />
        ) : null}

        {/*
          Timing alignment score — Canva page-05:
          pale beige card · left eyebrow/title/summary · hairline rule ·
          right large score + gold /100 · track · LOW/MID/HIGH label.
        */}
        <div
          className="mc-pdf-card mc-pdf-alignment-score"
          style={{
            display: "flex",
            alignItems: "center",
            background: paleTan,
            border: `1px solid ${border}`,
            borderRadius: 18,
            padding: "18px 20px",
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        >
          <div style={{ flex: "1 1 auto", minWidth: 0, paddingRight: 18 }}>
            <p
              style={{
                margin: 0,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: tanGold,
              }}
            >
              Timing alignment score
            </p>
            {label.length > 0 ? (
              <p
                style={{
                  margin: "8px 0 0",
                  fontFamily: displayFontFamily,
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.01em",
                  color: deepRed,
                }}
              >
                {label}
              </p>
            ) : null}
            {summary.length > 0 ? (
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: mutedGray,
                  maxWidth: 360,
                }}
              >
                {clipPdfText(summary, 160)}
              </p>
            ) : null}
          </div>

          <div
            aria-hidden="true"
            style={{
              width: 1,
              alignSelf: "stretch",
              background: border,
              flexShrink: 0,
              marginTop: 4,
              marginBottom: 4,
            }}
          />

          <div
            style={{
              width: 176,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              paddingLeft: 18,
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: displayFontFamily,
                lineHeight: 1,
                textAlign: "center",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <span
                style={{
                  fontSize: 40,
                  fontWeight: 700,
                  color: deepRed,
                  letterSpacing: "-0.03em",
                }}
              >
                {String(score)}
              </span>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: tanGold,
                  marginLeft: 6,
                  letterSpacing: "0.02em",
                }}
              >
                /100
              </span>
            </p>
            <div
              role="progressbar"
              aria-valuenow={score}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Timing alignment score"
              style={{
                width: "100%",
                height: 11,
                borderRadius: 999,
                backgroundColor: cardWhite,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: `${score}%`,
                  height: "100%",
                  borderRadius: 999,
                  backgroundColor: barColor,
                }}
              />
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: tanGold,
                textAlign: "center",
              }}
            >
              {bandLabel}
            </p>
          </div>
        </div>

        <div
          className="mc-pdf-grid-2x2 mc-pdf-alignment-guidance"
          style={{
            alignItems: "stretch",
            flex: "0 0 auto",
          }}
        >
          <PdfGuidanceCard
            tone="do"
            lines={doThis}
            emptyFallback="No do-this guidance is listed for this month."
          />
          <PdfGuidanceCard
            tone="dont"
            lines={dontDo}
            emptyFallback="No watch-outs are listed for this month."
          />
        </div>

        {noteBody.length > 0 ? (
          <div
            className="mc-pdf-card"
            style={{
              display: "flex",
              gap: 14,
              alignItems: "center",
              background: paleTan,
              border: `1px solid ${border}`,
              borderRadius: 16,
              padding: "12px 14px",
              boxSizing: "border-box",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
                width: 88,
                flexShrink: 0,
              }}
            >
              {mountains.length > 0 ? (
                <img
                  src={mountains}
                  alt=""
                  className="mc-pdf-alignment-note-seal"
                />
              ) : (
                <div
                  aria-hidden="true"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: `${tanGold}28`,
                    border: `1px solid ${tanGold}55`,
                  }}
                />
              )}
              <p
                style={{
                  margin: 0,
                  fontSize: 7,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: mutedGray,
                  textAlign: "center",
                  lineHeight: 1.25,
                }}
              >
                Consultation brief complete
              </p>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: gold,
                }}
              >
                {noteTitle}
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: charcoal,
                }}
              >
                {noteBody}
              </p>
              {noteSign.length > 0 ? (
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 10,
                    fontWeight: 600,
                    color: mutedGray,
                    textAlign: "right",
                  }}
                >
                  {noteSign}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {insight.length > 0 ? (
          <aside
            className="mc-pdf-dark-callout"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              background: insightDark,
              borderRadius: 14,
              padding: "12px 14px",
              boxSizing: "border-box",
              width: "100%",
              flexShrink: 0,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: `${gold}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Sparkles size={18} color={gold} strokeWidth={2} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: gold,
                }}
              >
                Executive insight
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontFamily: displayFontFamily,
                  fontSize: 13,
                  fontStyle: "italic",
                  lineHeight: 1.4,
                  color: "#e8d5a8",
                }}
              >
                {insight}
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: 12,
                flexShrink: 0,
                alignItems: "flex-start",
              }}
            >
              {ALIGNMENT_INSIGHT_PILLARS.map((pillar) => {
                const Icon = PILLAR_ICON[pillar.key];
                return (
                  <div
                    key={pillar.key}
                    style={{
                      width: 64,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      textAlign: "center",
                    }}
                  >
                    <Icon size={16} color={gold} strokeWidth={2} aria-hidden="true" />
                    <p
                      style={{
                        margin: 0,
                        fontSize: 7,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        lineHeight: 1.25,
                        color: "#e8d5a8",
                      }}
                    >
                      {pillar.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </aside>
        ) : null}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterConvergence;
